import React, { useState } from 'react';
import {
  Filter, Ban, ChevronDown, ChevronRight, CheckCircle2, Circle,
  User as UserIcon, Wrench, MessageSquare,
} from 'lucide-react';
import { Card, Avatar } from '../../ui-v2';
import { Badge } from '../../ui-v2.5';
// 3/11 (B2) · las etiquetas de estado, de etiquetasEstado.js (antes, una copia aquí con ui-v2).
import {
  ESTADO_CITA, MODALIDAD, etiquetaDe, esCitaAbierta, esCitaCancelada, textoMotivo,
} from '../compartidos/etiquetasEstado';

export default function SeccionCitas({
  citas, totalCitas, tecnicos,
  filtroEstado, setFiltroEstado,
  filtroTecnico, setFiltroTecnico,
  onCancelar,
  // 3/11 · sólo para quien gestiona (lo decide quien llama; el servidor lo vuelve a comprobar).
  onReabrir = null,
}) {
  const [expandidas, setExpandidas] = useState(() => new Set());

  function toggle(citaId) {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(citaId)) next.delete(citaId);
      else next.add(citaId);
      return next;
    });
  }

  return (
    <Card className="overflow-hidden p-0 mb-6">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-medium text-slate-900">Citas</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {citas.length} de {totalCitas} mostradas
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Filter className="w-3.5 h-3.5" />
            Filtrar
          </div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white"
          >
            <option value="todas">Todas</option>
            <option value="vivas">Vivas (reservadas + confirmadas)</option>
            <option value="reservada">Reservadas</option>
            <option value="confirmada">Confirmadas</option>
            <option value="realizadas">Realizadas</option>
            <option value="no_asistio">No asistió</option>
            <option value="canceladas">Canceladas</option>
          </select>
          <select
            value={filtroTecnico}
            onChange={(e) => setFiltroTecnico(e.target.value)}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white"
          >
            <option value="">Todos los técnicos</option>
            {tecnicos.map((t) => (
              <option key={t.id} value={t.id}>{t.nombre_completo}</option>
            ))}
          </select>
        </div>
      </div>

      {citas.length === 0 ? (
        <div className="px-5 py-12 text-center text-sm text-slate-400">
          No hay citas que coincidan con los filtros.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {citas.map((c) => (
            <FilaCita
              key={c.id}
              cita={c}
              expandida={expandidas.has(c.id)}
              onToggle={() => toggle(c.id)}
              onCancelar={() => onCancelar(c)}
              onReabrir={onReabrir ? () => onReabrir(c) : null}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function FilaCita({ cita, expandida, onToggle, onCancelar, onReabrir }) {
  const meta = etiquetaDe(ESTADO_CITA, cita.estado);
  const cancelable = esCitaAbierta(cita.estado);
  const cancelada = esCitaCancelada(cita.estado);
  // Sólo una realizada CON hora: la registrada sin cita se deshace, no se reabre.
  const reabrible = !!onReabrir && cita.estado === 'realizada' && !!cita.hora_inicio;
  const tieneSubs = cita.subtareas_total > 0;
  const subsCompletas = cita.subtareas_completadas;
  // 3/11 · el progreso cuenta en abiertas y realizadas; en una cancelada o «no asistió» las marcas
  // se conservan pero no cuentan.
  const cuentaProgreso = tieneSubs && (cancelable || cita.estado === 'realizada');
  const motivo = textoMotivo(cita, cuentaProgreso ? { hechas: subsCompletas, total: cita.subtareas_total } : null);

  return (
    <li className={cancelada ? 'bg-slate-50' : ''}>
      {/* Cancelada: atenuada con el fondo, NO con opacity (la insignia perdería contraste). */}
      <div className={'px-5 py-4 transition-colors ' + (cancelada ? '' : 'hover:bg-slate-50/60')}>
        <div className="flex items-start gap-4">
          {/* Toggle expandir */}
          <button
            type="button"
            onClick={onToggle}
            className="mt-0.5 w-6 h-6 grid place-items-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"
            aria-label={expandida ? 'Contraer' : 'Expandir'}
          >
            {expandida ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {/* Fecha + hora */}
          <div className="shrink-0 w-32">
            <div className="text-sm text-slate-900 font-medium">
              {formatFechaCorta(cita.fecha)}
            </div>
            <div className="text-xs text-slate-500 tabular-nums">
              {/* Registrada sin cita (24/10): sin hora; se dice cómo se hizo. */}
              {cita.hora_inicio ? `${cita.hora_inicio}–${cita.hora_fin}` : `Sin cita · ${etiquetaDe(MODALIDAD, cita.modalidad).label}`}
            </div>
          </div>

          {/* Usuario + técnico */}
          <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <Avatar name={cita.usuario.nombre_completo} size="sm" />
              <div className="min-w-0">
                <div className="text-sm text-slate-900 truncate">
                  {cita.usuario.nombre_completo}
                </div>
                {cita.usuario.email && (
                  <div className="text-[11px] text-slate-400 truncate">{cita.usuario.email}</div>
                )}
                {/* Lotes · se le quitó de la campaña; su cita se conserva. */}
                {cita.usuario.ya_no_esta && (
                  <div className="text-[11px] text-amber-700">Ya no está en la campaña</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-full bg-slate-100 grid place-items-center shrink-0">
                <Wrench className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] text-slate-400 uppercase tracking-wide">Técnico</div>
                <div className="text-sm text-slate-700 truncate">{cita.tecnico.nombre_completo}</div>
              </div>
            </div>
          </div>

          {/* Estado + subtareas + acciones */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex flex-col items-end gap-1">
              <Badge tone={meta.tone}>{meta.label}</Badge>
              {cuentaProgreso && (cita.estado === 'realizada' || subsCompletas > 0) && (
                <span className={
                  'text-[11px] tabular-nums ' +
                  (subsCompletas === cita.subtareas_total ? 'text-emerald-700' : 'text-amber-700')
                }>
                  {subsCompletas}/{cita.subtareas_total} subtareas
                </span>
              )}
            </div>
            {cancelable && (
              <button
                type="button"
                onClick={onCancelar}
                title="Cancelar cita"
                aria-label="Cancelar cita"
                className="w-8 h-8 grid place-items-center rounded-md hover:bg-red-50 transition-colors"
              >
                <Ban className="w-4 h-4 text-red-600" />
              </button>
            )}
            {reabrible && (
              <button
                type="button"
                onClick={onReabrir}
                className="px-2 py-1 text-xs rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              >
                Reabrir
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Panel expandido */}
      {expandida && (
        <div className="px-5 pb-5 pl-[60px] pt-1 bg-slate-50/40 border-t border-slate-100">
          {/* 3/11 · el motivo, según el estado (cierre incompleto, cancelación o no asistió). Texto plano. */}
          {motivo && (
            <div className={'mb-3 text-xs rounded px-2 py-1.5 border '
              + (cita.estado === 'realizada' ? 'text-amber-900 bg-amber-50 border-amber-200' : 'text-slate-700 bg-white border-slate-200')}
            >
              {motivo}
            </div>
          )}
          {tieneSubs && !cuentaProgreso && (
            <p className="mb-2 text-[11px] text-slate-600">
              Las marcas se conservan, pero no cuentan como progreso: la cita no se hizo.
            </p>
          )}
          {cita.notas && (
            <div className="mb-3 flex items-start gap-2 text-xs">
              <MessageSquare className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
              <p className="text-slate-600 italic">{cita.notas}</p>
            </div>
          )}

          {tieneSubs ? (
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wide mb-2">
                Subtareas
              </div>
              <ul className="space-y-1.5">
                {cita.subtareas.map((s) => (
                  <li key={s.id} className="flex items-start gap-2.5 text-sm">
                    {s.completada ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    ) : (
                      <Circle className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className={s.completada ? 'text-slate-800' : 'text-slate-500'}>
                        {s.nombre}
                      </div>
                      {s.descripcion && (
                        <div className="text-xs text-slate-500 mt-0.5">{s.descripcion}</div>
                      )}
                      {s.completada && s.completada_por && (
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                          <UserIcon className="w-3 h-3" />
                          Marcada por <strong className="font-medium text-slate-700">{s.completada_por.nombre_completo}</strong>
                          {s.fecha_completado && (
                            <> · {formatFechaHora(s.fecha_completado)}</>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">
              La campaña no tiene subtareas configuradas.
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function formatFechaCorta(s) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short',
  });
}
function formatFechaHora(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
